import {
  type Poly,
  polyAdd,
  polyConst,
  polyConstantValue,
  polyDivideByConstant,
  polyIsConstant,
  polyIsZero,
  polyMul,
  polyNeg,
  polySub,
  polyTotalDegree,
  polyVar,
} from "@/lib/tool-kit/algebra/poly";
import {
  divR,
  intR,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * A small, safe parser for algebra written the way students type it.
 *
 *     expression := term (("+" | "−") term)*
 *     term       := unary (("×" | "/") unary | implicit)*      left to right
 *     unary      := ("+" | "−") unary | power
 *     power      := primary ("^" exponent)?
 *     primary    := number | letter | "(" expression ")"
 *
 * Every letter is its own variable, so `3xy` is 3·x·y, and a number or bracket next to
 * a letter or bracket multiplies without a star: `2(x + 1)`, `(x + 1)(x − 1)`, `x(x + 2)`.
 * Division is allowed only by a non-zero constant, because the result must stay a
 * polynomial: `x/3` and `(2x + 1)/4` parse, `1/x` is refused with its own message.
 * Numbers are read exactly (`0.5`, `0,5` and `1/2` are the same value). There is no
 * `eval`.
 */
export type ParseErrorCode =
  | "empty"
  | "too-long"
  | "character"
  | "unexpected"
  | "end"
  | "bracket"
  | "divide-by-variable"
  | "divide-by-zero"
  | "exponent"
  | "too-large";

export interface ParseError {
  code: ParseErrorCode;
  token: string;
}

export type ParseResult =
  | { ok: true; poly: Poly }
  | { ok: false; error: ParseError };

export const MAX_INPUT_LENGTH = 400;
const MAX_DEGREE = 40;
const MAX_EXPONENT = 24;
const MAX_TERMS = 400;

type Token =
  | { kind: "number"; value: Rational; text: string }
  | { kind: "letter"; name: string }
  | { kind: "op"; op: "+" | "-" | "*" | "/" | "^" | "(" | ")" };

const SUPERSCRIPT_RUN = /[⁰¹²³⁴⁵⁶⁷⁸⁹]+/g;
const SUPERSCRIPT_DIGITS: Record<string, string> = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
};
const NUMBER_AT_START = /^\d+(?:[.,]\d+)?|^[.,]\d+/;
const LETTER = /^[A-Za-z]/;
const SPACE = /\s+/g;

/** Typographic operators and superscripts reduced to the ASCII the tokenizer reads. */
export function normalizeAlgebra(input: string): string {
  return input
    .replace(/[−–—‒]/g, "-")
    .replace(/[×·⋅∙]/g, "*")
    .replace(/[÷∕⁄]/g, "/")
    .replace(/[［【]/g, "(")
    .replace(/[］】]/g, ")")
    .replace(/[[{]/g, "(")
    .replace(/[\]}]/g, ")")
    .replace(
      SUPERSCRIPT_RUN,
      (run) =>
        `^${run
          .split("")
          .map((c) => SUPERSCRIPT_DIGITS[c] ?? "")
          .join("")}`
    )
    .replace(SPACE, " ")
    .trim();
}

function fail(code: ParseErrorCode, token = ""): ParseResult {
  return { ok: false, error: { code, token } };
}

class ParseFailure extends Error {
  readonly detail: ParseError;
  constructor(code: ParseErrorCode, token = "") {
    super(code);
    this.detail = { code, token };
  }
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let rest = text;
  while (rest.length > 0) {
    const first = rest[0];
    if (first === " ") {
      rest = rest.slice(1);
      continue;
    }
    const number = NUMBER_AT_START.exec(rest);
    if (number) {
      const value = parseRational(number[0].replace(",", "."));
      if (value === null) {
        throw new ParseFailure("character", number[0]);
      }
      tokens.push({ kind: "number", value, text: number[0] });
      rest = rest.slice(number[0].length);
      continue;
    }
    if (LETTER.test(first)) {
      tokens.push({ kind: "letter", name: first });
      rest = rest.slice(1);
      continue;
    }
    if ("+-*/^()".includes(first)) {
      tokens.push({ kind: "op", op: first as "+" });
      rest = rest.slice(1);
      continue;
    }
    throw new ParseFailure("character", first);
  }
  return tokens;
}

class Parser {
  private position = 0;
  private readonly tokens: Token[];

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  parse(): Poly {
    const result = this.expression();
    if (this.position < this.tokens.length) {
      const token = this.tokens[this.position];
      let text = "";
      if (token.kind === "op") {
        text = token.op;
      } else if (token.kind === "number") {
        text = token.text;
      }
      throw new ParseFailure(text === ")" ? "bracket" : "unexpected", text);
    }
    return result;
  }

  private peek(): Token | undefined {
    return this.tokens[this.position];
  }

  private isOp(op: string): boolean {
    const token = this.peek();
    return token?.kind === "op" && token.op === op;
  }

  private expression(): Poly {
    let result = this.term();
    while (this.isOp("+") || this.isOp("-")) {
      const subtract = this.isOp("-");
      this.position += 1;
      const right = this.term();
      result = subtract ? polySub(result, right) : polyAdd(result, right);
      this.guardSize(result);
    }
    return result;
  }

  private startsOperand(): boolean {
    const token = this.peek();
    if (!token) {
      return false;
    }
    return token.kind === "letter" || (token.kind === "op" && token.op === "(");
  }

  private term(): Poly {
    let result = this.unary();
    for (;;) {
      if (this.isOp("*")) {
        this.position += 1;
        result = polyMul(result, this.unary());
      } else if (this.isOp("/")) {
        this.position += 1;
        const divisor = this.unary();
        if (!polyIsConstant(divisor)) {
          throw new ParseFailure("divide-by-variable");
        }
        if (polyIsZero(divisor)) {
          throw new ParseFailure("divide-by-zero");
        }
        result = polyDivideByConstant(result, polyConstantValue(divisor));
      } else if (this.startsOperand()) {
        result = polyMul(result, this.power());
      } else {
        return result;
      }
      this.guardSize(result);
    }
  }

  private unary(): Poly {
    if (this.isOp("-")) {
      this.position += 1;
      return polyNeg(this.unary());
    }
    if (this.isOp("+")) {
      this.position += 1;
      return this.unary();
    }
    return this.power();
  }

  private power(): Poly {
    const base = this.primary();
    if (!this.isOp("^")) {
      return base;
    }
    this.position += 1;
    const exponent = this.exponent();
    if (polyIsConstant(base) && exponent < 0) {
      const value = polyConstantValue(base);
      if (polyIsZero(base)) {
        throw new ParseFailure("divide-by-zero");
      }
      return polyConst(rationalPower(divR(intR(1), value), -exponent));
    }
    if (exponent < 0) {
      throw new ParseFailure("divide-by-variable");
    }
    return this.raise(base, exponent);
  }

  private raise(base: Poly, exponent: number): Poly {
    if (polyTotalDegree(base) * exponent > MAX_DEGREE) {
      throw new ParseFailure("too-large");
    }
    let result: Poly = polyConst(intR(1));
    for (let i = 0; i < exponent; i += 1) {
      result = polyMul(result, base);
      this.guardSize(result);
    }
    return result;
  }

  private exponent(): number {
    let sign = 1;
    if (this.isOp("-")) {
      sign = -1;
      this.position += 1;
    } else if (this.isOp("+")) {
      this.position += 1;
    }
    const token = this.peek();
    if (token?.kind !== "number") {
      throw new ParseFailure("exponent");
    }
    this.position += 1;
    const value = token.value;
    if (value.den !== BigInt(1) || value.num > BigInt(MAX_EXPONENT)) {
      throw new ParseFailure(
        value.den === BigInt(1) ? "too-large" : "exponent",
        token.text
      );
    }
    return sign * Number(value.num);
  }

  private primary(): Poly {
    const token = this.peek();
    if (!token) {
      throw new ParseFailure("end");
    }
    this.position += 1;
    if (token.kind === "number") {
      return polyConst(token.value);
    }
    if (token.kind === "letter") {
      return polyVar(token.name);
    }
    if (token.op === "(") {
      const inner = this.expression();
      if (!this.isOp(")")) {
        throw new ParseFailure("bracket");
      }
      this.position += 1;
      return inner;
    }
    throw new ParseFailure(
      token.op === ")" ? "bracket" : "unexpected",
      token.op
    );
  }

  private guardSize(p: Poly): void {
    if (Object.keys(p).length > MAX_TERMS) {
      throw new ParseFailure("too-large");
    }
  }
}

function rationalPower(base: Rational, exponent: number): Rational {
  let result = intR(1);
  for (let i = 0; i < exponent; i += 1) {
    result = {
      num: result.num * base.num,
      den: result.den * base.den,
    };
  }
  return divR(result, intR(1));
}

/** Parse one algebraic expression. */
export function parseAlgebra(input: string): ParseResult {
  const text = normalizeAlgebra(input);
  if (text === "") {
    return fail("empty");
  }
  if (text.length > MAX_INPUT_LENGTH) {
    return fail("too-long");
  }
  try {
    return { ok: true, poly: new Parser(tokenize(text)).parse() };
  } catch (error) {
    if (error instanceof ParseFailure) {
      return { ok: false, error: error.detail };
    }
    throw error;
  }
}
